import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value as string); });
    try {
      await fetch("https://readdy.ai/api/form/d7cpmqc5q56ra7tufbag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#f8faff]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <span className="text-xs font-medium tracking-widest uppercase text-[#3b82f6] font-['Inter']">Get In Touch</span>
            <h2 className="font-['DM_Serif_Display'] text-4xl md:text-5xl text-[#0f172a] mt-3 mb-6 leading-tight">
              Let&apos;s Build<br /><em>Something Together</em>
            </h2>
            <p className="text-[#475569] text-sm leading-relaxed font-['Inter'] mb-10">
              Whether you&apos;re an educator, institution, researcher, or learner — we&apos;d love to hear from you. Reach out to explore partnerships, collaborations, or simply to say hello.
            </p>
            <div className="flex flex-col gap-5">
              {[
                { icon: "ri-mail-line", label: "Email", value: "contact@cashlearn.org" },
                { icon: "ri-time-line", label: "Response Time", value: "Within 72 hours" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#eff6ff] flex-shrink-0">
                    <i className={`${item.icon} text-[#3b82f6]`} />
                  </div>
                  <div>
                    <div className="text-xs text-[#94a3b8] font-['Inter']">{item.label}</div>
                    <div className="text-sm text-[#0f172a] font-medium font-['Inter'] mt-0.5">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-[#dbeafe]">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#eff6ff] mb-4">
                  <i className="ri-check-line text-[#1d4ed8] text-2xl" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0f172a] mb-2">Message Sent!</h3>
                <p className="text-[#475569] text-sm font-['Inter']">Thank you for reaching out. We&apos;ll be in touch within 48 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#94a3b8] font-['Inter'] mb-1.5 block">First Name</label>
                    <input name="firstName" required type="text" placeholder="Amara" className="w-full h-11 px-4 rounded-xl border border-[#dbeafe] focus:border-[#3b82f6] outline-none text-sm text-[#0f172a] font-['Inter'] bg-[#f8faff] transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-[#94a3b8] font-['Inter'] mb-1.5 block">Last Name</label>
                    <input name="lastName" required type="text" placeholder="Osei" className="w-full h-11 px-4 rounded-xl border border-[#dbeafe] focus:border-[#3b82f6] outline-none text-sm text-[#0f172a] font-['Inter'] bg-[#f8faff] transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#94a3b8] font-['Inter'] mb-1.5 block">Email</label>
                  <input name="email" required type="email" placeholder="you@example.com" className="w-full h-11 px-4 rounded-xl border border-[#dbeafe] focus:border-[#3b82f6] outline-none text-sm text-[#0f172a] font-['Inter'] bg-[#f8faff] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-[#94a3b8] font-['Inter'] mb-1.5 block">Role</label>
                  <select name="role" className="w-full h-11 px-4 rounded-xl border border-[#dbeafe] focus:border-[#3b82f6] outline-none text-sm text-[#0f172a] font-['Inter'] bg-[#f8faff] transition-colors cursor-pointer">
                    <option value="">Select your role</option>
                    <option value="educator">Educator</option>
                    <option value="researcher">Researcher</option>
                    <option value="institution">Institution</option>
                    <option value="learner">Learner</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94a3b8] font-['Inter'] mb-1.5 block">Message</label>
                  <textarea name="message" required rows={4} maxLength={500} placeholder="Tell us how we can help..." onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl border border-[#dbeafe] focus:border-[#3b82f6] outline-none text-sm text-[#0f172a] font-['Inter'] bg-[#f8faff] transition-colors resize-none" />
                  <div className="text-right text-xs text-[#94a3b8] mt-1 font-['Inter']">{charCount}/500</div>
                </div>
                <button type="submit" disabled={loading || charCount > 500}
                  className="w-full h-12 bg-[#1d4ed8] hover:bg-[#1e40af] disabled:opacity-60 text-white rounded-xl text-sm font-medium font-['Inter'] transition-colors cursor-pointer whitespace-nowrap">
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
